import cx from 'classnames';
import Link from "next/link";
import { useRouter } from "next/router";
import { FC, ReactElement, useContext, useEffect } from "react";
import { StoreContext, namespace } from '@/context';
import styles from '../index.module.scss';
import useCheckLoginState from '@/hooks/useCheckLoginState';
import userCenter from '@/constants/userCenter';
import { useGlobalContext } from '@/components/ResponsiveLayout';

/** 导航配置类型 */
export interface HomeSidebarListOption {
  key: string;
  /** 跳转路径 */
  path: string;
  /** 导航名称 */
  label: string;
  /** 导航图标 */
  icon: {
    active: ReactElement | ReactElement[],
    normal: ReactElement | ReactElement[],
  };
  /** 点击事件 */
  onClick?: () => void;
  /** 徽标数 */
  badge?: number;
}

interface SideItems {
  options: HomeSidebarListOption[]
}

interface SidebarListProps {
  options: HomeSidebarListOption[];
  activeKey: string;
  onSelect?: (path: string, label: string) => void;
  disableResponsive?: boolean;
  compact?: boolean;
}

export const SidebarList: FC<SidebarListProps> = ({ options, activeKey, onSelect, disableResponsive = false, compact = false }) => {
  return (
    <ul className={styles.sidebarList}>
      {options.map(({key, icon, label, path, onClick, badge}) => (
        <li
          key={key}
          onClick={() => {
            if (onSelect) onSelect(path, label);
            if (onClick) onClick();
          }}
          className={cx(styles.sidebarItem, activeKey === path ? styles.activeNav : '')}
          style={compact ? { padding: '8px', borderRadius: '22px', justifyContent: 'center' } : disableResponsive ? { padding: '8px 12px', borderRadius: '24px' } : undefined}
        >
          <div className={cx(styles.tooltip, styles.tooltipRight)} data-tooltip={label} >
            <div className={styles.icon} style={compact ? { marginRight: 0 } : disableResponsive ? { marginRight: 10 } : undefined}>
              {activeKey === path ? icon.active : icon.normal}
              {!!badge && badge > 0 && (
                <span className={styles.badge}>
                  {badge > 99 ? '99+' : badge}
                </span>
              )}
            </div>
          </div>
          <span className={styles.title} style={compact ? { display: 'none' } : disableResponsive ? { display: 'inline' } : undefined}>{label}</span>
        </li>
      ))}
    </ul>
  )
}

//左侧导航栏列表
const SideItems: FC<SideItems> = ({options}) => {
  const { pathname } = useRouter();
  const { isLogout } = useCheckLoginState();
  const { state } = useContext(StoreContext);
  const { track, TRACK_EVENTS } = state[namespace.app].sensors || {};
  const { activeNav, setActiveNav } = useGlobalContext();

  const onTrack = (label: string, path: string) => {
    track?.(TRACK_EVENTS?.[namespace.home].accountBarButtonClick.name, {
      button_name: label,
      position: 'Bottom',
    }, namespace.home);
    setActiveNav(path);
    window.open(path, 'self');
  }

  useEffect(() => {
    if (pathname !== activeNav) {
      setActiveNav(`/pro${pathname}`);
    }
  }, [pathname]);

  return (
    <SidebarList 
      options={options} 
      activeKey={activeNav} 
      onSelect={(path, label) => {
        // 埋点
        track?.(TRACK_EVENTS?.[namespace.home].accountBarButtonClick.name, {
          button_name: label,
          position: 'Bottom',
        }, namespace.home);
        // 设置当前导航
        setActiveNav(path);
      }}
    />
  )
}

export default SideItems;
